import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { BrandLogo } from "@/components/Brand";
import { API_BASE } from "@/lib/api";
import { cn } from "@/lib/utils";

const T = {
  en: {
    toggle: "हिंदी", duties: "What you will do", shift: "Shift", off: "Weekly off",
    stay: "Stay & food", needs: "What we need", joining: "Joining", apply: "Apply now",
    filled: "Positions filled", filledNote: "This advert is closed. Please check our other openings.",
    salary: "Salary", month: "/ month", all: "All jobs", adult: "18 years or older",
    languages: "Languages", experience: "Experience",
  },
  hi: {
    toggle: "English", duties: "आपको क्या करना है", shift: "शिफ़्ट", off: "साप्ताहिक छुट्टी",
    stay: "रहना और खाना", needs: "हमें क्या चाहिए", joining: "जॉइनिंग", apply: "अभी अप्लाई करें",
    filled: "पद भर गए हैं", filledNote: "यह विज्ञापन बंद हो गया है। बाकी नौकरियाँ देखें।",
    salary: "सैलरी", month: "/ महीना", all: "सभी नौकरियाँ", adult: "18 साल या उससे ज़्यादा",
    languages: "भाषाएँ", experience: "अनुभव",
  },
};

const rupees = (n) => (n === null || n === undefined ? "—" : `₹${Number(n).toLocaleString("en-IN")}`);

function JobCard({ job, lang, t, closed }) {
  const title = lang === "hi" ? (job.title_hi || job.title_en) : (job.title_en || job.title_hi);
  const duties = (lang === "hi" ? job.duties_hi : job.duties_en) || job.duties_en || [];
  const shift = (lang === "hi" ? job.shift_hi : job.shift_en) || job.shift_en;
  const stay = (lang === "hi" ? job.stay_food_hi : job.stay_food_en) || job.stay_food_en;
  return (
    <article className="rounded-2xl border bg-card p-5" data-testid={`public-job-${job.target_id}`}>
      <h2 className="font-display text-2xl font-bold tracking-tight">{title}</h2>
      <p className="mt-1 text-sm text-muted-foreground" data-testid={`public-job-place-${job.target_id}`}>
        {[job.branch_label, job.city, job.state].filter(Boolean).join(" · ")}
      </p>
      <p className="mt-3 text-base font-semibold" data-testid={`public-job-salary-${job.target_id}`}>
        {t.salary}: {rupees(job.salary_min)} – {rupees(job.salary_max)} <span className="text-sm font-normal text-muted-foreground">{t.month}</span>
      </p>

      {duties.length > 0 && (
        <>
          <p className="mt-5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t.duties}</p>
          <ul className="mt-1.5 space-y-1 text-sm">
            {duties.map((d, i) => <li key={i} className="flex gap-2"><span className="text-brand-pink">•</span>{d}</li>)}
          </ul>
        </>
      )}

      <dl className="mt-5 grid grid-cols-[110px_1fr] gap-x-3 gap-y-1.5 text-sm">
        {[[t.shift, shift], [t.off, job.weekly_off], [t.stay, stay],
          [t.experience, job.req_min_experience],
          [t.languages, (job.req_languages || []).join(", ")],
          [t.joining, job.joining_window]].filter(([, v]) => v).map(([k, v]) => (
          <div key={k} className="contents">
            <dt className="text-xs text-muted-foreground">{k}</dt>
            <dd>{v}</dd>
          </div>
        ))}
        {job.req_adult && (
          <div className="contents">
            <dt className="text-xs text-muted-foreground">{t.needs}</dt>
            <dd>{t.adult}</dd>
          </div>
        )}
      </dl>

      {!closed && (
        <a href={job.apply_url} data-testid={`public-job-apply-${job.target_id}`}
          className="mt-5 block rounded-2xl bg-brand-pink px-5 py-3.5 text-center text-base font-semibold text-white">
          {t.apply}
        </a>
      )}
    </article>
  );
}

/** `/jobs/<slug>` — one campaign's public advert, Hindi / English, with schema.org JobPosting
 *  structured data while it is Live and nothing at all once it is not. */
export default function JobPage() {
  const { slug } = useParams();
  const [lang, setLang] = useState("hi");
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);
  const t = T[lang];

  useEffect(() => {
    axios.get(`${API_BASE}/public/jobs/${slug}`)
      .then(({ data: d }) => setData(d))
      .catch(() => setFailed(true));
  }, [slug]);

  useEffect(() => {
    if (!data) return;
    document.title = `${data.name} · goSTOPS`;
    const added = [];
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = data.noindex ? "noindex" : "index,follow";
    document.head.appendChild(meta);
    added.push(meta);
    // A Paused or Ended advert carries no JobPosting at all — it must not keep telling a search
    // engine that it is open.
    (data.jsonld || []).forEach((posting) => {
      const tag = document.createElement("script");
      tag.type = "application/ld+json";
      tag.setAttribute("data-jobposting", "1");
      tag.text = JSON.stringify(posting);
      document.head.appendChild(tag);
      added.push(tag);
    });
    return () => added.forEach((n) => n.remove());
  }, [data]);

  if (failed) {
    return (
      <div className="grid min-h-screen place-items-center px-4" data-testid="public-job-missing">
        <p className="text-sm text-muted-foreground">This job page is not available.</p>
      </div>
    );
  }
  if (!data) return <div className="min-h-screen" />;

  return (
    <div className="min-h-screen bg-background" data-testid="public-job-page">
      <header className="border-b">
        <div className="mx-auto flex h-14 max-w-3xl items-center justify-between px-4">
          <BrandLogo className="h-7" />
          <button type="button" onClick={() => setLang((l) => (l === "hi" ? "en" : "hi"))}
            data-testid="public-job-lang-toggle"
            className="rounded-full border px-3.5 py-1.5 text-xs font-semibold">
            {t.toggle}
          </button>
        </div>
      </header>
      <main className="mx-auto max-w-3xl px-4 py-8">
        <Link to="/jobs" className="text-xs text-muted-foreground underline-offset-2 hover:underline"
          data-testid="public-job-all-link">← {t.all}</Link>
        <h1 className="mt-3 font-display text-3xl font-bold tracking-tight sm:text-4xl">{data.name}</h1>

        {data.positions_filled && (
          <p className={cn("mt-4 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-base font-semibold text-amber-900")}
            data-testid="public-job-filled">
            {t.filled}
            <span className="mt-0.5 block text-xs font-normal">{t.filledNote}</span>
          </p>
        )}

        <div className="mt-6 space-y-4">
          {data.jobs.map((job) => (
            <JobCard key={job.target_id} job={job} lang={lang} t={t} closed={data.positions_filled} />
          ))}
        </div>
      </main>
    </div>
  );
}
